'use client'

import { TriangleAlert } from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { memo, useCallback, useMemo, useRef } from 'react'
import { Button } from '@/components/button'
import { CopyCode } from '@/features/code/copy-code'
import { getPartnershipConfig, PartnershipConfig } from '@/features/code/partnership-config'
import { PartnershipCountdown } from '@/features/code/partnership-countdown'
import { PartnershipLogos } from '@/features/code/partnership-logos'
import { getHasTopPageBanner } from '@/features/navigation/navigation-mobile'
import { SvgOutlinedArrowRight } from '@/icons/svg-outlined-arrow-right'
import { Locale } from '@/setup/locales'
import { usePathname } from '@/setup/routing'
import { cn } from '@/utils/styles/cn'

function PartnershipAlert({ partnership }: { partnership: PartnershipConfig }) {
    const t = useTranslations()

    if (!partnership.showAlert || !partnership.translationPrefix) return null

    return (
        <div className="mt-6 w-full rounded-lg border border-warning/30 bg-warning/10 p-4">
            <div className="flex items-start gap-3">
                <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning" />

                <div className="text-sm text-warning">
                    <p className="font-semibold">
                        {t(`${partnership.translationPrefix}.alertTitle`)}
                    </p>
                    <p>{t(`${partnership.translationPrefix}.alertDescription`)}</p>
                </div>
            </div>
        </div>
    )
}

export const LayoutCode = memo(() => {
    const searchParams = useSearchParams()
    const pathname = usePathname()
    const locale = useLocale()
    const t = useTranslations()

    const code = searchParams.get('code')
    const rawAmountOfMonths = searchParams.get('a')

    const containerRef = useRef<HTMLDivElement>(null)

    const onScollDownClick = useCallback(() => {
        window.scrollTo({
            top: containerRef.current?.clientHeight || 0,
            behavior: 'smooth',
        })
    }, [])

    // Determine if we're on a partnership page and get the config
    const partnershipContext = useMemo(() => {
        const isHomePage = pathname === '/'

        const pathSegment = pathname.split('/').filter(Boolean)[0]
        const partnership = pathSegment ? getPartnershipConfig(pathSegment) : undefined

        if (partnership) {
            return {
                isPartnershipPage: true,
                isHomePage: false,
                partnership,
                amountOfMonths: partnership.amountOfMonths,
                translationPrefix: partnership.translationPrefix || 'baseLayout.code',
            }
        }

        if (isHomePage) {
            return {
                isPartnershipPage: false,
                isHomePage: true,
                partnership: undefined,
                amountOfMonths: parseInt(rawAmountOfMonths || '0', 10),
                translationPrefix: 'baseLayout.code',
            }
        }

        return null
    }, [pathname, rawAmountOfMonths])

    const hasTopPageBanner = getHasTopPageBanner(locale as Locale)

    // Hide if no code, not on valid page, or no amount of months
    if (!code || !partnershipContext || !partnershipContext.amountOfMonths) return null

    const { partnership, amountOfMonths, translationPrefix } = partnershipContext

    return (
        <div
            ref={containerRef}
            className={cn(
                'w-full py-14 px-5 md:p-20 flex flex-col items-center justify-center bg-black mt-16 md:mt-0',
                hasTopPageBanner && 'pt-32'
            )}
        >
            <div className="flex flex-col max-w-[100%] md:max-w-[440px] items-center">
                {partnership && <PartnershipLogos partnership={partnership} />}
                {partnership && <PartnershipCountdown partnership={partnership} />}

                <h3 className="text-[24px] text-white mb-5 text-center">
                    {t(`${translationPrefix}.title`, { amountOfMonths })} 🙌
                </h3>

                <p className="text-white/50 text-center text-[16px] w-full mb-8 md:mb-12">
                    {t(`${translationPrefix}.description`)}
                </p>

                <CopyCode code={code} />

                {partnership && <PartnershipAlert partnership={partnership} />}

                <div className="mt-8 md:mt-14" />

                <Button onClick={onScollDownClick} styleType="primary" shape="pill" height="52px">
                    <div className="rotate-90">
                        <SvgOutlinedArrowRight size={16} color="black" />
                    </div>

                    <span className="ml-2">{t('baseLayout.code.explain')}</span>
                </Button>
            </div>
        </div>
    )
})

LayoutCode.displayName = 'LayoutCode'
